// Sélecteur de rouleaux à la création/édition de vente.
// Mode auto FIFO : répartit le m² demandé sur les rouleaux les plus anciens en priorité.
// Mode manuel : l'utilisateur édite les m² par rouleau.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RouleauListItem, SortieStockInput } from '@cockpit/shared';
import { api } from '../../lib/api';
import { formatDate, formatEuro } from '../../lib/format';

interface Props {
  referenceId: number;
  largeurCm: 200 | 400 | null;
  m2CentiTarget: number; // m² × 100 souhaité pour cette ligne
  value: SortieStockInput[];
  onChange: (sorties: SortieStockInput[]) => void;
}

function fmtM(cm: number): string {
  return `${(cm / 100).toFixed(2).replace('.', ',')} m`;
}

function fmtM2Centi(c: number): string {
  return `${(c / 100).toFixed(2).replace('.', ',')} m²`;
}

// Calcule la longueur_cm à prélever pour atteindre un m2_centi sur un rouleau de largeur W.
// m2_centi = longueur_cm × W / 100 → longueur_cm = m2_centi × 100 / W
function longueurFromM2Centi(m2Centi: number, largeurCm: number): number {
  return Math.round((m2Centi * 100) / largeurCm);
}

function m2CentiFromLongueur(longueurCm: number, largeurCm: number): number {
  return Math.round((longueurCm * largeurCm) / 100);
}

// Répartition automatique FIFO : on consomme dans l'ordre jusqu'à atteindre la cible.
function repartirAutoFIFO(rouleaux: RouleauListItem[], m2CentiTarget: number): SortieStockInput[] {
  const out: SortieStockInput[] = [];
  let restant = m2CentiTarget;
  for (const r of rouleaux) {
    if (restant <= 0) break;
    const dispoCentiM2 = m2CentiFromLongueur(r.longueur_restante_cm, r.largeur_cm);
    if (dispoCentiM2 <= 0) continue;
    const aPrendreCentiM2 = Math.min(restant, dispoCentiM2);
    const longueur = Math.min(
      r.longueur_restante_cm,
      longueurFromM2Centi(aPrendreCentiM2, r.largeur_cm),
    );
    if (longueur <= 0) continue;
    const m2Centi = m2CentiFromLongueur(longueur, r.largeur_cm);
    out.push({ rouleau_id: r.id, longueur_prelevee_cm: longueur, m2_centi: m2Centi });
    restant -= m2Centi;
  }
  return out;
}

export function RouleauPicker({
  referenceId,
  largeurCm,
  m2CentiTarget,
  value,
  onChange,
}: Props): JSX.Element {
  const [mode, setMode] = useState<'auto' | 'manuel'>('auto');

  const query = useQuery({
    queryKey: ['rouleaux', 'picker', referenceId, largeurCm],
    queryFn: () =>
      api.rouleaux.list({
        reference_id: referenceId,
        largeur_cm: largeurCm === null ? undefined : largeurCm,
        statut_disponible: 'true',
      }),
    enabled: referenceId > 0,
  });

  const rouleaux = useMemo(() => query.data?.items ?? [], [query.data]);

  // En mode auto : recalcule automatiquement quand m2CentiTarget change.
  useEffect(() => {
    if (mode !== 'auto') return;
    if (rouleaux.length === 0) {
      onChange([]);
      return;
    }
    if (m2CentiTarget <= 0) {
      onChange([]);
      return;
    }
    const next = repartirAutoFIFO(rouleaux, m2CentiTarget);
    onChange(next);
    // ESLint exhaustive-deps : on volontairement n'inclut pas onChange dans deps pour éviter une boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rouleaux, m2CentiTarget]);

  const totalPrev = value.reduce((s, x) => s + x.m2_centi, 0);
  const diff = totalPrev - m2CentiTarget;

  // Coût pondéré et marge prévue
  const coutPondereCentimes = useMemo(() => {
    if (value.length === 0) return null;
    let sumC = 0;
    let sumM2 = 0;
    for (const v of value) {
      const r = rouleaux.find((x) => x.id === v.rouleau_id);
      if (r === undefined) continue;
      sumC += v.m2_centi * r.cout_eur_par_m2_centimes;
      sumM2 += v.m2_centi;
    }
    return sumM2 > 0 ? Math.round(sumC / sumM2) : null;
  }, [value, rouleaux]);

  const handleManualChange = (rouleauId: number, m2CentiStr: string): void => {
    const f = Number.parseFloat(m2CentiStr.replace(',', '.'));
    const m2Centi = Number.isFinite(f) ? Math.round(f * 100) : 0;
    const r = rouleaux.find((x) => x.id === rouleauId);
    if (r === undefined) return;
    const longueur = longueurFromM2Centi(m2Centi, r.largeur_cm);
    const next = [...value.filter((v) => v.rouleau_id !== rouleauId)];
    if (m2Centi > 0) {
      next.push({ rouleau_id: rouleauId, longueur_prelevee_cm: longueur, m2_centi: m2Centi });
    }
    onChange(next);
  };

  if (referenceId <= 0) return <></>;
  if (query.isLoading) {
    return <div className="text-xs text-slate-500 italic">Chargement des rouleaux…</div>;
  }
  if (rouleaux.length === 0) {
    return (
      <div className="text-xs text-amber-300 bg-amber-900/20 border border-amber-900/50 rounded-md px-3 py-2">
        Aucun rouleau disponible pour cette référence. La vente sera enregistrée sans sortie de
        stock (mode historique). Renseigner un coût estimé pour calculer la marge.
      </div>
    );
  }

  const valueByRouleau = new Map(value.map((v) => [v.rouleau_id, v]));

  return (
    <div className="border border-slate-700 rounded-md p-3 bg-slate-900/60">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          Sélection rouleaux ({rouleaux.length} dispo)
        </div>
        <div className="flex rounded-md overflow-hidden border border-slate-700 text-xs">
          <button
            type="button"
            onClick={(): void => setMode('auto')}
            className={`px-2 py-1 ${mode === 'auto' ? 'bg-floryon-green text-white' : 'bg-slate-900 text-slate-400'}`}
          >
            Auto FIFO
          </button>
          <button
            type="button"
            onClick={(): void => setMode('manuel')}
            className={`px-2 py-1 ${mode === 'manuel' ? 'bg-floryon-green text-white' : 'bg-slate-900 text-slate-400'}`}
          >
            Manuel
          </button>
        </div>
      </div>

      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase">
          <tr>
            <th className="text-left py-1">Container</th>
            <th className="text-right py-1">Largeur</th>
            <th className="text-right py-1">Restant</th>
            <th className="text-right py-1">Coût €/m²</th>
            <th className="text-right py-1">À prélever (m²)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rouleaux.map((r) => {
            const sel = valueByRouleau.get(r.id);
            const m2Sel = sel?.m2_centi ?? 0;
            const m2SelStr = m2Sel > 0 ? (m2Sel / 100).toFixed(2).replace('.', ',') : '';
            return (
              <tr key={r.id}>
                <td className="py-1.5 text-slate-300">
                  {r.container_reference} <span className="text-slate-500">#{r.id}</span>
                  <div className="text-[10px] text-slate-600">
                    {formatDate(r.container_date_commande)}
                  </div>
                </td>
                <td className="py-1.5 text-right text-slate-300">{r.largeur_cm} cm</td>
                <td className="py-1.5 text-right text-slate-300">{fmtM(r.longueur_restante_cm)}</td>
                <td className="py-1.5 text-right text-slate-300">
                  {formatEuro(r.cout_eur_par_m2_centimes)}
                </td>
                <td className="py-1.5 text-right">
                  {mode === 'auto' ? (
                    <span className="text-slate-200">{fmtM2Centi(m2Sel)}</span>
                  ) : (
                    <input
                      type="text"
                      value={m2SelStr}
                      onChange={(e): void => handleManualChange(r.id, e.target.value)}
                      className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-xs text-right w-24"
                      placeholder="0,00"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-3 pt-2 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div>
          Total prélevé :{' '}
          <span className={`font-semibold ${diff === 0 ? 'text-emerald-300' : 'text-amber-300'}`}>
            {fmtM2Centi(totalPrev)}
          </span>
          {diff !== 0 && (
            <span className="ml-2 text-amber-300">
              {diff > 0 ? 'trop' : 'manque'} {fmtM2Centi(Math.abs(diff))}
            </span>
          )}
        </div>
        {coutPondereCentimes !== null && (
          <div className="text-slate-300">
            Coût pondéré locké :{' '}
            <span className="font-semibold">{formatEuro(coutPondereCentimes)}/m²</span>
          </div>
        )}
      </div>
    </div>
  );
}
